import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  GitBranch,
  TrendingUp,
  Building2,
  Globe,
  BarChart3,
  Users,
  XCircle,
  PlayCircle,
  ChevronRight,
} from 'lucide-react';
import { apiClient } from '../lib/api/config.js';
import { format } from 'date-fns';
import { TableScroll } from '../components/ui/table-scroll.js';

interface StoryThread {
  thread_id: string;
  thread_type: string;
  thread_key: string;
  display_name: string;
  status: string;
  member_count: number;
  date_from: string | null;
  date_to: string | null;
  detected_at: string;
  thread_metadata: Record<string, any>;
}

const TYPE_ICONS: Record<string, typeof GitBranch> = {
  entity: Building2,
  activity: TrendingUp,
  sector: Globe,
  macro: BarChart3,
  shared_event: Users,
};

const TYPE_COLORS: Record<string, string> = {
  entity: 'bg-primary-100 text-primary-700',
  activity: 'bg-warning-100 text-warning-700',
  sector: 'bg-success-100 text-success-700',
  macro: 'bg-purple-100 text-purple-700',
  shared_event: 'bg-red-100 text-red-700',
};

const STATUS_COLORS: Record<string, string> = {
  detected: 'bg-blue-100 text-blue-700',
  candidate: 'bg-yellow-100 text-yellow-700',
  queued: 'bg-orange-100 text-orange-700',
  generating: 'bg-purple-100 text-purple-700',
  generated: 'bg-green-100 text-green-700',
  dismissed: 'bg-grey-100 text-grey-500',
};

async function fetchThreads(params: { page: number; thread_type?: string; status?: string }) {
  const response = await apiClient.get('/admin/threads', { params });
  return response.data;
}

async function fetchThreadStats() {
  const response = await apiClient.get('/admin/threads/stats');
  return response.data;
}

export function StoryThreadsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data: threads, isLoading } = useQuery({
    queryKey: ['story-threads', page, typeFilter, statusFilter],
    queryFn: () =>
      fetchThreads({
        page,
        thread_type: typeFilter || undefined,
        status: statusFilter || undefined,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ['story-thread-stats'],
    queryFn: fetchThreadStats,
  });

  const dismissMutation = useMutation({
    mutationFn: (threadId: string) => apiClient.post(`/admin/threads/${threadId}/dismiss`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['story-threads'] });
      queryClient.invalidateQueries({ queryKey: ['story-thread-stats'] });
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-grey-900">Story Threads</h1>
        <p className="mt-2 text-grey-500">
          Detected patterns for synthesis content generation
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {['entity', 'activity', 'sector', 'macro', 'shared_event'].map((type) => {
            const Icon = TYPE_ICONS[type] || GitBranch;
            return (
              <div
                key={type}
                className="bg-surface rounded-xl border border-grey-200 p-4 shadow-sm cursor-pointer hover:border-primary-300"
                onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="h-5 w-5 text-grey-400" />
                  <div>
                    <p className="text-sm text-grey-500 capitalize">{type}</p>
                    <p className="text-2xl font-bold text-grey-900">
                      {stats.by_type[type] || 0}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center space-x-4 mb-6">
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          className="border border-grey-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All types</option>
          <option value="entity">Entity</option>
          <option value="activity">Activity</option>
          <option value="sector">Sector</option>
          <option value="macro">Macro</option>
          <option value="shared_event">Shared Event</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-grey-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          <option value="detected">Detected</option>
          <option value="candidate">Candidate</option>
          <option value="queued">Queued</option>
          <option value="generating">Generating</option>
          <option value="generated">Generated</option>
          <option value="failed">Failed</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      {/* Threads Table */}
      <div className="bg-surface rounded-xl border border-grey-200 shadow-md overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-grey-500">Loading threads...</div>
        ) : !threads?.items?.length ? (
          <div className="p-8 text-center text-grey-500">No threads detected yet</div>
        ) : (
          <TableScroll>
          <table className="min-w-full divide-y divide-grey-200">
            <thead className="bg-grey-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Type</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Thread</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Status</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Members</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Date Range</th>
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-grey-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-100">
              {threads.items.map((thread: StoryThread) => {
                const Icon = TYPE_ICONS[thread.thread_type] || GitBranch;
                return (
                  <tr key={thread.thread_id} className="hover:bg-grey-50">
                    <td className="px-3 sm:px-6 py-4">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${TYPE_COLORS[thread.thread_type] || 'bg-grey-100 text-grey-600'}`}>
                        <Icon className="h-3 w-3" />
                        <span className="capitalize">{thread.thread_type}</span>
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <p className="font-medium text-grey-900">{thread.display_name}</p>
                      <p className="text-sm text-grey-500">{thread.thread_key}</p>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[thread.status] || 'bg-grey-100'}`}>
                        {thread.status}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-grey-700">{thread.member_count}</td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-grey-500">
                      {thread.date_from && thread.date_to ? (
                        <>
                          {format(new Date(thread.date_from), 'MMM d')} —{' '}
                          {format(new Date(thread.date_to), 'MMM d, yyyy')}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right space-x-2">
                      {thread.status === 'detected' && (
                        <>
                          <Link
                            to={`/threads/${thread.thread_id}`}
                            className="text-primary-600 hover:text-primary-700"
                            title="Review members and queue synthesis"
                          >
                            <PlayCircle className="h-5 w-5 inline" />
                          </Link>
                          <button
                            onClick={() => dismissMutation.mutate(thread.thread_id)}
                            className="text-grey-400 hover:text-danger-500"
                            title="Dismiss"
                          >
                            <XCircle className="h-5 w-5 inline" />
                          </button>
                        </>
                      )}
                      <Link
                        to={`/threads/${thread.thread_id}`}
                        className="text-grey-400 hover:text-grey-600"
                        title="View detail"
                      >
                        <ChevronRight className="h-5 w-5 inline" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TableScroll>
        )}

        {/* Pagination */}
        {threads && threads.pages > 1 && (
          <div className="px-6 py-3 bg-grey-50 border-t border-grey-200 flex items-center justify-between">
            <p className="text-sm text-grey-500">
              Page {threads.page} of {threads.pages} ({threads.total} threads)
            </p>
            <div className="space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-grey-300 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= threads.pages}
                className="px-3 py-1 border border-grey-300 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
